import { NavLink } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import type { AdminUser } from '@manaro/contracts'
import { navigationFor } from '@/shared/config/navigation.ts'
import { cn } from '@/shared/lib/cn.ts'
import { Avatar } from '@/shared/ui/avatar.tsx'
import { Logo } from '@/shared/ui/logo.tsx'
import { COUNTER_LABELS, useNavCounters } from './nav-counters.ts'

const linkClass = (active: boolean) =>
  cn(
    'flex items-center gap-2.5 rounded-nav px-2.5 py-2.5 text-[14.5px] transition-colors',
    active ? 'bg-brand-tint font-semibold text-brand-ink' : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
  )

export function Sidebar({ user, onLogout }: { user: AdminUser; onLogout: () => void }) {
  const counters = useNavCounters(user)

  return (
    // Desktop only: phones get the bottom tab bar instead.
    <aside className="sticky top-0 hidden h-screen w-[228px] shrink-0 flex-col gap-6.5 border-r border-line bg-surface-soft px-4.5 py-6.5 md:flex">
      <div className="flex items-center gap-2.5 px-2">
        <div className="min-w-0 flex-1">
          <Logo height={18} />
          <div className="mt-1 text-[11.5px] text-ink-muted">админка сервиса</div>
        </div>
      </div>

      <nav className="flex flex-col gap-0.5">
        {navigationFor(user.sections).map(({ to, label, icon: Icon, end, soon, counter }) =>
          soon ? (
            <span
              key={to}
              title="Раздел появится позже"
              aria-disabled
              className="flex cursor-not-allowed items-center gap-2.5 rounded-nav px-2.5 py-2.5 text-[14.5px] text-ink-faint"
            >
              <Icon className="size-[17px] shrink-0" strokeWidth={1.8} />
              {label}
              <span className="rounded-md bg-neutral-tint px-1.5 py-0.5 text-[10.5px] font-semibold text-neutral-ink">
                скоро
              </span>
            </span>
          ) : (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => linkClass(isActive)}>
              {({ isActive }) => (
                <>
                  <Icon className="size-[17px] shrink-0" strokeWidth={isActive ? 1.9 : 1.8} />
                  {label}
                  {counter && counters[counter] > 0 && (
                    <span className="tabular ml-auto rounded-full bg-warn-tint px-1.5 text-[11.5px] leading-[18px] font-semibold text-warn-ink">
                      {counters[counter]}
                      <span className="sr-only"> {COUNTER_LABELS[counter]}</span>
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ),
        )}
      </nav>

      <div className="flex-1" />

      <div className="flex items-center gap-2.5 border-t border-line px-2 pt-3.5">
        <Avatar name={user.name} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-medium">{user.name}</div>
          <div className="text-[12px] text-ink-muted">{user.role}</div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          title="Выйти"
          aria-label="Выйти"
          className="rounded-md p-1.5 text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </aside>
  )
}
